import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Chip,
  Button,
  IconButton,
  Avatar,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowLeft,
  Clock,
  Share2,
  CheckCircle2,
  Sparkles,
  BookOpen,
  ArrowRight,
  PhoneCall,
  Calendar,
} from 'lucide-react';
import { DEFAULT_HERO_IMAGE, fetchArticleBySlug, fetchPublishedArticles } from '../data/blogData';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const initialArticle = location.state?.article?.slug === slug ? location.state.article : null;
  const [article, setArticle] = useState(initialArticle);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(!initialArticle);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [copiedNotification, setCopiedNotification] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState('');

  // Fetch article from CMS by slug
  useEffect(() => {
    const controller = new AbortController();
    const navArticle = location.state?.article?.slug === slug ? location.state.article : null;
    if (navArticle) {
      setArticle(navArticle);
      setIsLoading(false);
      return () => controller.abort();
    }
    setIsLoading(true);
    setArticle(null);

    fetchArticleBySlug(slug, controller.signal)
      .then((data) => {
        setArticle(data);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('Error fetching article:', err);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => controller.abort();
  }, [slug, location.state]);

  // Fetch related articles (other CMS posts, exclude current slug)
  useEffect(() => {
    const controller = new AbortController();

    fetchPublishedArticles(controller.signal)
      .then((all) => {
        setRelatedArticles(all.filter((a) => a.slug !== slug).slice(0, 3));
      })
      .catch(() => {});

    return () => controller.abort();
  }, [slug]);

  // Scroll to top on slug change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Reading progress tracker + TOC highlighting
  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollTop || document.body.scrollTop;
      const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      if (windowHeight > 0) {
        setScrollProgress((totalScroll / windowHeight) * 100);
      }

      // Check current visible section for TOC
      if (article?.headings?.length) {
        const sections = article.headings.map((h) => document.getElementById(h.id)).filter(Boolean);
        for (let i = sections.length - 1; i >= 0; i--) {
          const rect = sections[i].getBoundingClientRect();
          if (rect.top <= 140) {
            setActiveSectionId(sections[i].id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [article]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article?.title,
        text: article?.excerpt,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      setCopiedNotification(true);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ py: 16, textAlign: 'center', backgroundColor: '#FFFFFF', minHeight: '60vh' }}>
        <CircularProgress size={44} sx={{ color: '#2563EB', mb: 2 }} />
        <Typography sx={{ color: '#64748B', fontSize: '0.95rem' }}>
          Loading article...
        </Typography>
      </Box>
    );
  }

  // Not found
  if (!article) {
    return (
      <Box sx={{ py: 16, textAlign: 'center', backgroundColor: '#FFFFFF', minHeight: '60vh' }}>
        <Container maxWidth="sm">
          <BookOpen size={48} color="#94A3B8" style={{ marginBottom: 16 }} />
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A', mb: 2 }}>
            Article Not Found
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748B', mb: 4 }}>
            The article you are searching for might have been moved or updated.
          </Typography>
          <Button
            component={RouterLink}
            to="/blog"
            variant="contained"
            startIcon={<ArrowLeft size={18} />}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: '#2563EB',
              '&:hover': { backgroundColor: '#1D4ED8' },
            }}
          >
            Back to Blog Directory
          </Button>
        </Container>
      </Box>
    );
  }

  const tocItems = article.headings || [];

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', backgroundColor: '#FFFFFF' }}>
      {/* 1. TOP FIXED READING PROGRESS BAR */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '3px',
          width: `${scrollProgress}%`,
          background: 'linear-gradient(90deg, #2563EB 0%, #2563EB 100%)',
          zIndex: 9999,
          transition: 'width 0.1s ease-out',
        }}
      />

      {/* 2. ARTICLE HEADER SECTION */}
      <Box
        sx={{
          pt: { xs: 4, md: 6 },
          pb: { xs: 4, md: 5 },
          backgroundColor: '#FFFFFF',
        }}
      >
        <Container maxWidth="lg">
          {/* Back Navigation Link */}
          <Box sx={{ mb: 3 }}>
            <Button
              component={RouterLink}
              to="/blog"
              startIcon={<ArrowLeft size={16} />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                color: '#2563EB',
                fontSize: '0.88rem',
                p: 0,
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: '#1D4ED8',
                },
              }}
            >
              Back to blog
            </Button>
          </Box>

          <Box sx={{ maxWidth: 880 }}>
            {/* Category Pill */}
            <Chip
              label={article.category}
              sx={{
                border: '1px solid rgba(37, 99, 235, 0.3)',
                backgroundColor: 'rgba(37, 99, 235, 0.08)',
                color: '#1D4ED8',
                fontWeight: 700,
                fontSize: '0.75rem',
                height: 26,
                mb: 2.5,
              }}
            />

            {/* Article Headline */}
            <Typography
              variant="h1"
              sx={{
                fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
                fontWeight: 800,
                fontSize: { xs: '1.9rem', sm: '2.6rem', md: '3.1rem' },
                lineHeight: 1.18,
                letterSpacing: '-0.03em',
                color: '#0F172A',
                mb: 2.5,
              }}
            >
              {article.title}
            </Typography>

            {/* Lead Excerpt */}
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1.1rem', sm: '1.24rem' },
                color: '#475569',
                lineHeight: 1.65,
                mb: 4,
                fontWeight: 400,
              }}
            >
              {article.excerpt}
            </Typography>

            {/* Author & Meta Row */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                py: 2.5,
                borderTop: '1px solid rgba(15, 23, 42, 0.08)',
                borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    fontSize: '0.9rem',
                    fontWeight: 800,
                    color: '#FFFFFF',
                    background: 'linear-gradient(135deg, #0F172A 0%, #2563EB 100%)',
                  }}
                >
                  {article.author.initials || 'CT'}
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: '#0F172A', fontSize: '0.92rem', lineHeight: 1.2 }}>
                    {article.author.name}
                  </Typography>
                  <Typography sx={{ color: '#64748B', fontSize: '0.78rem' }}>
                    {article.author.role}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: '#64748B', fontSize: '0.82rem' }}>
                  <Clock size={15} />
                  <span>{article.readTime}</span>
                </Box>
                <Typography sx={{ color: '#94A3B8', fontSize: '0.82rem' }}>
                  {article.publishedDate}
                </Typography>
                <IconButton
                  onClick={handleShare}
                  aria-label="Share article"
                  size="small"
                  sx={{
                    border: '1px solid rgba(15, 23, 42, 0.12)',
                    color: '#475569',
                    '&:hover': {
                      borderColor: '#2563EB',
                      color: '#2563EB',
                      backgroundColor: 'rgba(37, 99, 235, 0.06)',
                    },
                  }}
                >
                  <Share2 size={16} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* 3. HERO IMAGE SECTION */}
      <Container maxWidth="lg" sx={{ mb: { xs: 5, md: 8 } }}>
        <Box
          sx={{
            width: '100%',
            aspectRatio: { xs: '16 / 9', md: '16 / 8.5' },
            borderRadius: '20px',
            overflow: 'hidden',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            boxShadow: '0 16px 36px -12px rgba(15, 23, 42, 0.08)',
          }}
        >
          <Box
            component="img"
            src={article.heroImage}
            alt={article.title}
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = DEFAULT_HERO_IMAGE;
            }}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        </Box>
      </Container>

      {/* 4. MAIN TWO-COLUMN CONTENT GRID */}
      <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 12 } }}>
        <Grid container spacing={{ xs: 4, lg: 7 }}>
          {/* Main Article Body (Left Column) */}
          <Grid item xs={12} lg={8}>
            {/* Table of Contents Box ("In this article") */}
            {tocItems.length > 0 && (
              <Box
                sx={{
                  p: { xs: 3, sm: 4 },
                  borderRadius: '18px',
                  backgroundColor: '#F8FAFC',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  mb: 5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.76rem',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: '#2563EB',
                    mb: 2.5,
                  }}
                >
                  In this article
                </Typography>
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {tocItems.map((toc) => (
                    <Box component="li" key={toc.id}>
                      <Box
                        component="a"
                        href={`#${toc.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          const el = document.getElementById(toc.id);
                          if (el) {
                            const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
                            window.scrollTo({ top: y, behavior: 'smooth' });
                          }
                        }}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'flex-start',
                          gap: 1.25,
                          fontSize: '0.94rem',
                          color: '#475569',
                          textDecoration: 'none',
                          fontWeight: 500,
                          lineHeight: 1.5,
                          pl: toc.level === 'h3' ? 2 : 0,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            color: '#2563EB',
                            transform: 'translateX(3px)',
                          },
                        }}
                      >
                        <CheckCircle2
                          size={17}
                          color="#2563EB"
                          style={{ flexShrink: 0, marginTop: 3 }}
                        />
                        <span>{toc.title}</span>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Rich Article Content */}
            <Box
              sx={{
                color: '#334155',
                fontSize: { xs: '1.02rem', md: '1.08rem' },
                lineHeight: 1.8,
                '& p': {
                  mb: 3,
                },
                '& .lead-text': {
                  fontSize: { xs: '1.15rem', md: '1.24rem' },
                  lineHeight: 1.75,
                  color: '#1E293B',
                  fontWeight: 450,
                  mb: 3.5,
                },
                '& h2': {
                  fontSize: { xs: '1.5rem', sm: '1.85rem' },
                  fontWeight: 800,
                  letterSpacing: '-0.025em',
                  color: '#0F172A',
                  mt: 6,
                  mb: 2,
                  scrollMarginTop: '100px',
                },
                '& h3': {
                  fontSize: { xs: '1.25rem', sm: '1.4rem' },
                  fontWeight: 700,
                  color: '#0F172A',
                  mt: 4.5,
                  mb: 1.5,
                  scrollMarginTop: '100px',
                },
                '& blockquote': {
                  borderLeft: '4px solid #2563EB',
                  pl: 3,
                  py: 1,
                  my: 3,
                  fontStyle: 'italic',
                  color: '#1E293B',
                  backgroundColor: 'rgba(37, 99, 235, 0.04)',
                  borderRadius: '0 12px 12px 0',
                },
                '& ul, & ol': {
                  pl: 3,
                  mb: 3,
                },
                '& li': {
                  mb: 1,
                },
                '& a': {
                  color: '#2563EB',
                  fontWeight: 600,
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                },
                '& img': {
                  maxWidth: '100%',
                  borderRadius: '12px',
                  my: 3,
                },
                '& hr': {
                  border: 'none',
                  borderTop: '1px solid rgba(15, 23, 42, 0.1)',
                  my: 5,
                },
                '& figure': {
                  mx: 0,
                  my: 4,
                  '& figcaption': {
                    textAlign: 'center',
                    fontSize: '0.85rem',
                    color: '#94A3B8',
                    mt: 1,
                  },
                },
                '& .styled-checklist': {
                  listStyle: 'none',
                  p: 0,
                  my: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  '& li': {
                    position: 'relative',
                    pl: 3.5,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '0.6em',
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#2563EB',
                    },
                  },
                },
                '& .styled-numbered-list': {
                  listStyle: 'none',
                  counterReset: 'item',
                  p: 0,
                  my: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  '& li': {
                    position: 'relative',
                    pl: 5,
                    '&::before': {
                      content: 'counter(item)',
                      counterIncrement: 'item',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: '26px',
                      height: '26px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #0F172A 0%, #2563EB 100%)',
                      color: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                    },
                  },
                },
                '& .blog-callout-card': {
                  my: 5,
                  p: { xs: 3.5, sm: 5 },
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, #07101F 0%, #0E1F44 55%, #064E3B 100%)',
                  color: '#FFFFFF',
                  border: '1px solid rgba(37, 99, 235, 0.25)',
                  boxShadow: '0 20px 40px -10px rgba(7, 16, 31, 0.4)',
                  position: 'relative',
                  overflow: 'hidden',
                  '& .callout-tag': {
                    fontSize: '0.72rem',
                    fontWeight: 800,
                    letterSpacing: '0.1em',
                    color: '#34D399',
                    mb: 1.5,
                  },
                  '& h3': {
                    color: '#FFFFFF',
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    fontWeight: 800,
                    lineHeight: 1.3,
                    mt: 0,
                    mb: 1.5,
                  },
                  '& p': {
                    color: '#94A3B8',
                    fontSize: '0.96rem',
                    lineHeight: 1.6,
                    mb: 3,
                  },
                  '& .callout-btn': {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: '#2563EB',
                    color: '#07101F',
                    fontWeight: 800,
                    fontSize: '0.92rem',
                    px: 3,
                    py: 1.4,
                    borderRadius: '100px',
                    textDecoration: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: '#34D399',
                      transform: 'translateY(-2px)',
                    },
                  },
                },
                '& .faq-group': {
                  mt: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2.5,
                },
                '& .faq-item': {
                  p: 3,
                  borderRadius: '14px',
                  backgroundColor: '#F8FAFC',
                  border: '1px solid rgba(15, 23, 42, 0.06)',
                  '& h4': {
                    color: '#0F172A',
                    fontSize: '1.08rem',
                    fontWeight: 700,
                    mt: 0,
                    mb: 1,
                  },
                  '& p': {
                    color: '#64748B',
                    fontSize: '0.95rem',
                    mb: 0,
                    lineHeight: 1.6,
                  },
                },
              }}
              dangerouslySetInnerHTML={{ __html: article.contentHtml }}
            />

            {/* Tags Row */}
            <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid rgba(15, 23, 42, 0.08)' }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#94A3B8', textTransform: 'uppercase', mb: 2 }}>
                Topics covered
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {article.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    sx={{
                      backgroundColor: '#F1F5F9',
                      color: '#334155',
                      fontWeight: 600,
                      borderRadius: '8px',
                      fontSize: '0.78rem',
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Author Bio Box */}
            <Box
              sx={{
                mt: 6,
                p: 3.5,
                borderRadius: '18px',
                backgroundColor: '#F8FAFC',
                border: '1px solid rgba(15, 23, 42, 0.08)',
                display: 'flex',
                alignItems: 'center',
                gap: 2.5,
              }}
            >
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  fontWeight: 800,
                  fontSize: '1.1rem',
                  background: 'linear-gradient(135deg, #0F172A 0%, #2563EB 100%)',
                }}
              >
                {article.author.initials || 'CT'}
              </Avatar>
              <Box>
                <Typography sx={{ fontWeight: 800, color: '#0F172A', fontSize: '1.05rem' }}>
                  {article.author.name}
                </Typography>
                <Typography sx={{ color: '#64748B', fontSize: '0.88rem', mt: 0.5, lineHeight: 1.5 }}>
                  Insights and playbooks from the Coalitionify CRM & AI engineering team. Building practical technology that helps real estate revenue teams capture, automate, and close faster.
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Right Column: Sticky Sidebar Table of Contents & CTA Card */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ position: 'sticky', top: 100, display: 'flex', flexDirection: 'column', gap: 3.5 }}>
              {/* "On this page" Navigator */}
              {tocItems.length > 0 && (
                <Box
                  sx={{
                    p: 3,
                    borderRadius: '16px',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    boxShadow: '0 4px 20px rgba(15, 23, 42, 0.03)',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      color: '#94A3B8',
                      mb: 2,
                    }}
                  >
                    On this page
                  </Typography>
                  <Box
                    component="nav"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      borderLeft: '2px solid rgba(15, 23, 42, 0.08)',
                    }}
                  >
                    {tocItems.map((toc) => {
                      const isActive = activeSectionId === toc.id;
                      return (
                        <Box
                          component="a"
                          key={toc.id}
                          href={`#${toc.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            const el = document.getElementById(toc.id);
                            if (el) {
                              const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
                              window.scrollTo({ top: y, behavior: 'smooth' });
                            }
                          }}
                          sx={{
                            pl: toc.level === 'h3' ? 3.5 : 2,
                            py: 0.7,
                            ml: '-2px',
                            borderLeft: isActive ? '2px solid #2563EB' : '2px solid transparent',
                            color: isActive ? '#2563EB' : '#64748B',
                            fontWeight: isActive ? 700 : 500,
                            fontSize: toc.level === 'h3' ? '0.8rem' : '0.84rem',
                            textDecoration: 'none',
                            lineHeight: 1.4,
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              color: '#0F172A',
                            },
                          }}
                        >
                          {toc.title}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Sidebar Conversion Card */}
              <Box
                sx={{
                  p: 3.5,
                  borderRadius: '18px',
                  background: 'linear-gradient(155deg, #07101F 0%, #0E1F44 60%, #064E3B 100%)',
                  color: '#FFFFFF',
                  border: '1px solid rgba(37, 99, 235, 0.25)',
                  boxShadow: '0 16px 36px -10px rgba(7, 16, 31, 0.35)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#34D399', mb: 1.5 }}>
                  <Sparkles size={16} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    Revenue Intelligence
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', lineHeight: 1.3, color: '#FFFFFF', mb: 1.5 }}>
                  Scale your Real Estate Sales with Coalitionify
                </Typography>
                <Typography sx={{ color: '#94A3B8', fontSize: '0.86rem', lineHeight: 1.6, mb: 3 }}>
                  Stop lead leakage. Capture inbound queries instantly, trigger AI phone follow-ups, and track bookings effortlessly.
                </Typography>
                <Button
                  component={RouterLink}
                  to="/contact"
                  fullWidth
                  variant="contained"
                  endIcon={<ArrowRight size={16} />}
                  sx={{
                    py: 1.4,
                    borderRadius: '12px',
                    fontWeight: 700,
                    fontSize: '0.88rem',
                    textTransform: 'none',
                    backgroundColor: '#2563EB',
                    color: '#07101F',
                    '&:hover': {
                      backgroundColor: '#34D399',
                    },
                  }}
                >
                  Book a Product Demo
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* 5. RELATED ARTICLES SECTION (Sell.Do style 3-card grid) */}
      {relatedArticles.length > 0 && (
        <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: '#F8FAFC', borderTop: '1px solid rgba(15, 23, 42, 0.06)' }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#2563EB', mb: 0.5 }}>
                  Keep Exploring
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.5rem', sm: '1.85rem' }, color: '#0F172A' }}>
                  Related Reading
                </Typography>
              </Box>
              <Button
                component={RouterLink}
                to="/blog"
                endIcon={<ArrowRight size={16} />}
                sx={{ textTransform: 'none', fontWeight: 700, color: '#2563EB' }}
              >
                View all articles
              </Button>
            </Box>

            <Grid container spacing={3}>
              {relatedArticles.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                  <Box
                    component={RouterLink}
                    to={`/blog/${item.slug}`}
                    sx={{
                      textDecoration: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      borderRadius: '18px',
                      backgroundColor: '#FFFFFF',
                      border: '1px solid rgba(15, 23, 42, 0.08)',
                      overflow: 'hidden',
                      transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 20px 40px -15px rgba(15, 23, 42, 0.08)',
                        borderColor: 'rgba(37, 99, 235, 0.35)',
                        '& .related-img': {
                          transform: 'scale(1.05)',
                        },
                        '& .related-title': {
                          color: '#2563EB',
                        },
                      },
                    }}
                  >
                    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 10', overflow: 'hidden' }}>
                      <Box
                        component="img"
                        src={item.heroImage}
                        alt={item.title}
                        onError={(event) => {
                          event.currentTarget.onerror = null;
                          event.currentTarget.src = DEFAULT_HERO_IMAGE;
                        }}
                        className="related-img"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.5s ease',
                        }}
                      />
                      <Chip
                        label={item.category}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          backgroundColor: 'rgba(7, 16, 31, 0.72)',
                          backdropFilter: 'blur(6px)',
                          color: '#FFFFFF',
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          height: 24,
                        }}
                      />
                    </Box>
                    <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#64748B', fontSize: '0.75rem', mb: 1.5 }}>
                        <span>{item.publishedDate}</span>
                        <span>·</span>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Clock size={12} />
                          <span>{item.readTime}</span>
                        </Box>
                      </Box>
                      <Typography
                        className="related-title"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.05rem',
                          lineHeight: 1.35,
                          color: '#0F172A',
                          mb: 1.5,
                          transition: 'color 0.2s ease',
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#64748B',
                          fontSize: '0.85rem',
                          lineHeight: 1.6,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mb: 2,
                          flex: 1,
                        }}
                      >
                        {item.excerpt}
                      </Typography>
                      <Box sx={{ pt: 2, borderTop: '1px solid rgba(15, 23, 42, 0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#0F172A' }}>
                          {item.author.name}
                        </Typography>
                        <ArrowRight size={14} color="#2563EB" />
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Copy Notification Toast */}
      <Snackbar
        open={copiedNotification}
        autoHideDuration={2500}
        onClose={() => setCopiedNotification(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ borderRadius: '12px' }}>
          Article link copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BlogDetailPage;
